import React, { FC, useCallback, useEffect, useMemo, useState } from "react";
import { observer } from "mobx-react-lite";
import GalleryDetail from "@/pages/Gallery/components/GalleryDetail";
import Search, { gallerySeed } from "@/pages/Gallery/components/Search";
import { Box, Center, Flex, Image, Text, useBoolean } from "@chakra-ui/react";
import { loginStore } from "@/modules/login/store";
import { HeaderWithLogin } from "@/modules/login/ui";
import { onAlbum, onFollow } from "@/pages/Gallery/hooks";
import notFound from "@/assets/images/404/arts_404.png";
import { GalleryItemType, ImgAndPromptItemType } from "@/stores/gallery";
import getScrollBarProps from "@/styles/scrollBarProps";
import { getPromptDetail, getPromptList } from "./api";
import Gallery from "react-photo-gallery";
import GalleryItem from "./components/GalleryItem";
import { AlbumData } from "./components/Album";
import { AIMetaType } from "@/types/meta";

interface FormatPhotosItemType {
  src: string;
  width: number;
  height: number;
  key: string;
  original: string;
  likes?: number;
}

const scrollProps = getScrollBarProps({ scrollBarWidth: 0 });
const Arts: FC<any> = ({ ...props }) => {
  const {
    match: { params },
  } = props;
  const paramId = useMemo(() => params?.id, []);
  const size = "64px";
  const [isNotFound, setIsNotFound] = useState(false);
  const [list, setList] = useState<GalleryItemType[]>([]);
  const [isSimilar, setIsSimilar] = useState(true);
  const [current, setCurrent] = useState<number>(0);
  const [detailModalOpen, detailModalOp] = useBoolean(false);
  const [previewList, setPreviewList] = useState<ImgAndPromptItemType[]>([]);
  console.log("previewList: ", previewList);
  useEffect(() => {
    init();
  }, []);

  const init = () => {
    getPromptDetail({ image_id: Number(paramId) })
      .then((res: any) => {
        const current: ImgAndPromptItemType = res.data.list?.find(
          (it: ImgAndPromptItemType) => it.image_id === +paramId,
        );
        const isOpen = current.is_open;
        if (current.status === 0) {
          setPreviewList(res.data.list);

          setIsNotFound(false);
          const prompt = (JSON.parse(current.meta_json) as AIMetaType)?.data?.text;
          if (prompt && isOpen) {
            getPromptList({
              keywords: prompt,
              page_index: 1,
              page_size: 25,
              collapse: true,
              image_id: Number(paramId),
            })
              .then((res) => res)
              .then((res) => {
                if (!res.data.list?.length) {
                  getPromptList({
                    keywords: "",
                    page_index: 1,
                    page_size: 25,
                    seed: gallerySeed(true),
                    sort: "sort_default",
                    author: "author_all",
                    image_id: Number(paramId),
                    collapse: true,
                  })
                    .then((res) => res)
                    .then((res) => {
                      setList(res.data.list);
                    });
                  setIsSimilar(false);
                } else if (res.data.list?.length < 25) {
                  // 补全25张
                  getPromptList({
                    keywords: prompt,
                    page_index: 1,
                    page_size: 25,
                    seed: gallerySeed(true),
                    sort: "sort_default",
                    author: "author_all",
                    image_id: Number(paramId),
                    collapse: false,
                  })
                    .then((res) => res)
                    .then((res) => {
                      setList(res.data.list);
                    });
                } else {
                  setList(res.data.list);
                }
              });
          } else {
            getPromptList({
              keywords: "",
              page_index: 1,
              page_size: 25,
              seed: gallerySeed(true),
              sort: "sort_default",
              author: "author_all",
              collapse: true,
              image_id: Number(paramId),
            })
              .then((res) => res)
              .then((res) => {
                setList(res.data.list);
                setIsSimilar(false);
              });
          }
        } else {
          setIsNotFound(true);
        }
      })
      .catch(() => setIsNotFound(true));
  };

  useEffect(() => {
    loginStore.setGlobalLoading(false);
  }, []);

  const formatPhotos: FormatPhotosItemType[] = useMemo(
    () =>
      list?.map((item) => {
        return {
          src: item.image_url,
          width: item.width ?? 300,
          height: item.height ?? 300,
          key: item.image_id.toString(),
          img_id: item.image_id,
          original: item.original,
          user: item.user,
          likes: item.likes,
          is_like: item.is_like,
          is_album: item.is_album,
          album_id: item.album_id,
          album_image_id: item.album_image_id,
          album_name: item.album_name,
          meta_json: item.meta_json,
        };
      }),
    [list],
  );

  const fetchAndSetData = useCallback(
    (imageId: number) => {
      getPromptDetail({ image_id: Number(imageId) }).then((res: any) => {
        setPreviewList(res.data.list);
      });
    },
    [setPreviewList],
  );

  const handleCurrentDetailOperate = () => {
    fetchAndSetData(paramId);
  };

  const onListItemAlbum = (type: number, albumData: AlbumData) => {
    const newList = list.concat();
    newList.forEach((item) => {
      if (item.image_id === Number(albumData.image_id)) {
        if (type !== 2) {
          item.is_album = !item.is_album;
        }
        item.album_id = albumData.album_id;
        item.album_name = albumData.album_name;
        item.album_image_id = albumData.album_image_id;
      }
    });
    setList(newList);
  };

  const onListItemLike = (imageId: number, like: boolean) => {
    const newList = list.concat();
    const factor = like ? 1 : -1;
    newList.forEach((item) => {
      if (item.image_id === imageId) {
        if (item.likes) {
          item.likes += factor;
        } else {
          item.likes = Math.max(0, factor);
        }
        item.is_like = like;
      }
      return item;
    });

    setList(newList);
  };

  return (
    <Flex
      minW="1280px"
      w="100%"
      bgColor="#080808"
      flexShrink={0}
      bgRepeat="no-repeat"
      bgSize="contain"
    >
      <HeaderWithLogin />
      <Flex
        flexFlow={"column"}
        alignItems="stretch"
        paddingTop={size}
        w="100%"
        h="100vh"
        pb="64px"
        {...scrollProps}
        color="#fff"
      >
        <Search />
        <Center>
          <GalleryDetail
            currentID={paramId}
            onAlbum={handleCurrentDetailOperate}
            onFollow={handleCurrentDetailOperate}
            onLike={handleCurrentDetailOperate}
            onCancelLike={handleCurrentDetailOperate}
            previewList={previewList}
            style={isNotFound ? { display: "none" } : {}}
          />
        </Center>
        {isNotFound && (
          <Box fontSize="32px" mt="105px" userSelect="none">
            <Text textAlign="center">您查找的页面不见了</Text>
            <Text textAlign="center">可以尝试去艺术展寻找更多灵感哦</Text>
            <Image src={notFound} w="50vw" margin="0 auto" />
            <Text fontSize="12px" color="#999999" textAlign="center">
              垫图生成：美丽可爱的彩色房子，插图，参考画家 Muhammed Sajid ，壁纸，高清
            </Text>
          </Box>
        )}
        <Box pl="100px" pr="70px" mt="60px">
          <Text fontSize="18px" fontWeight={600} mb="24px">
            {isSimilar ? "更多相似推荐" : "更多精彩推荐"}
          </Text>
          <Box>
            {formatPhotos.length ? (
              <Gallery
                photos={formatPhotos}
                columns={5}
                direction="column"
                margin={15}
                renderImage={(data) => {
                  return (
                    <GalleryItem
                      list={list}
                      key={data.index}
                      setCurrent={setCurrent}
                      data={data}
                      setDetailVisible={(visible) => {
                        visible ? detailModalOp.on() : detailModalOp.off();
                      }}
                      onAlbum={onListItemAlbum}
                      onLike={(imageId) => onListItemLike(imageId, true)}
                      onCancelLike={(imageId) => onListItemLike(imageId, false)}
                    />
                  );
                }}
              />
            ) : null}
          </Box>
          {detailModalOpen && (
            <Center
              position="fixed"
              zIndex={5}
              left={0}
              right={0}
              top={0}
              bottom={0}
              background="rgba(0, 0, 0, 0.7)"
            >
              <GalleryDetail
                // opusId={5}
                currentID={current}
                backgroundColor="#27272A"
                onClose={() => {
                  history.back();
                  detailModalOp.off();
                }}
                onAlbum={onAlbum}
                onFollow={onFollow}
              />
            </Center>
          )}
        </Box>
      </Flex>
    </Flex>
  );
};
export default observer(Arts);
